import { css, type HTMLTemplateResult, render, type TemplateResult } from "lit";
import { Tag, Tags } from "../../schemas/tag";
import { hostStyles } from "../../styles/host.styles";
import { WmElement } from "../element";

/**
A component whose sole purpose is to regroup together a bunch of tags
and make them fit on one line (as a table cell).

When the element lacks width for the provided tags, those are removed
starting at the end and replaced by a count tag until they fit within the
given dimensions.
*/
export class WmTagCell extends WmElement {
  static readonly TAG_NAME = "wm-tag-cell";

  static styles = [
    hostStyles,
    css`
      :host {
        display: inline-block;
        width: 100%;
        overflow: hidden;
      }
    `,
  ];

  static properties = {
    template: { state: true },
  };

  private declare template: HTMLTemplateResult;
  #observer: ResizeObserver;
  #tags: Tags;

  constructor() {
    super();
    this.#observer = new ResizeObserver(() => {
      this.#fit();
    });
    this.#tags = [];
    this.template = this.#formatTags(this.#tags);
  }

  get tags() {
    return this.#tags;
  }

  set tags(v: Tags) {
    const result = Tags.safeParse(v);
    if (!result.success) {
      this.logger.warn("invalid tags, ignoring", v, result.error);
      return;
    }
    this.#tags = v;
    this.#fit();
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.#observer.observe(this);
  }

  override disconnectedCallback(): void {
    this.#observer.disconnect();
    super.disconnectedCallback();
  }

  protected override render() {
    return this.template;
  }

  async #fit() {
    // If we don't have tags, then we're done.
    if (this.tags.length === 0) {
      this.template = this.#formatTags(this.tags);
      return;
    }

    // If the tags already fit within the available bounds, then
    // we are also done.
    const boundingRect = this.getBoundingClientRect();
    const measuredRect = await this.#measure(this.#formatTags(this.tags));
    if (boundingRect.width >= measuredRect.width) {
      this.template = this.#formatTags(this.tags);
      return;
    }

    const originalTags = [...this.tags];
    let remainingCount = 0;
    while (true) {
      originalTags.pop();
      remainingCount++;
      const displayedTags = [
        ...originalTags,
        // Remaining tag chip.
        Tag.parse({ id: "", name: `+${remainingCount}` }),
      ];
      const measuredRect = await this.#measure(this.#formatTags(displayedTags));
      if (
        boundingRect.width >= measuredRect.width ||
        originalTags.length === 0
      ) {
        this.template = this.#formatTags(displayedTags);
        return;
      }
    }
  }

  async #measure(litTemplate: TemplateResult): Promise<DOMRect> {
    const sandbox = document.createElement("div");
    sandbox.style.position = "absolute";
    sandbox.style.visibility = "hidden";
    sandbox.style.margin = "0";
    sandbox.style.padding = "0";
    sandbox.style.pointerEvents = "none";
    await this.updateComplete;
    this.renderRoot.appendChild(sandbox);
    try {
      render(litTemplate, sandbox);
      const tags = [...sandbox.querySelectorAll("wa-tag")];
      await Promise.all(tags.map((t) => t.updateComplete));
      return sandbox.getBoundingClientRect();
    } finally {
      sandbox.remove();
    }
  }

  #formatTags(tags: Tags) {
    return Tags.format(tags, { size: "s" });
  }
}

customElements.define(WmTagCell.TAG_NAME, WmTagCell);

declare global {
  interface HTMLElementTagNameMap {
    [WmTagCell.TAG_NAME]: WmTagCell;
  }
}
